import React, { useEffect } from "react";
import { AutoComplete } from "rsuite";
import {
  Button,
  Flex,
  SwitchField,
  TextAreaField,
  SelectField,
  Label,
  Text,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import {
  useSymptomQuery,
  useCatalogSymptomsQuery,
} from "./api/useSymptomsQuery";
import { useSymptomsPutMutation } from "./api/useSymptomsMutation";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
const { z } = require("zod");

const symptomSchema = z.object({
  symptom: z
    .string({ message: "Symptom Required" })
    .min(1, { message: "Symptom Required" })
    .max(100, { message: "Must be 100 characters or fewer" }),
  symptomStartDate: z.union([
    z.number({ message: "Symptom Start Date Required" }),
    z.string({ message: "Symptom Start Date Required" }),
    z.date({ message: "Symptom Start Date Required" }),
  ]),
  symptomEndDate: z
    .union([
      z.number(),
      z.string(),
      z.date(),
    ])
    .optional()
    .nullish(),
  howStartExperience: z
    .string()
    .min(1, { message: "What do you experience? is required" })
    .max(250, { message: "Must be 250 characters or fewer" }),
  active: z.number().int(),
});

export default function SymptomsEditPage() {
  const navigate = useNavigate();
  const paramsUrl = useParams();
  const { data: userQueryData } = useUserQuery();
  const putSymptomMutation = useSymptomsPutMutation();

  const { isLoading, error, data } = useSymptomQuery(
    userQueryData?.patientUserId,
    paramsUrl.id
  );

  const { data: symptomsOptions, isLoading: isLoadingSymptomsCatalog } =
    useCatalogSymptomsQuery();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(symptomSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        symptom: data?.symptom || "",
        symptomStartDate: data?.symptomStartDate || null,
        symptomEndDate: data?.symptomEndDate || null,
        active: data?.active || 0,
        howStartExperience: data?.howStartExperience || "",
      });
    }
  }, [data]);

  const onSubmitForm = async (values) => {
    try {
      const formatStartDate = new Date(values.symptomStartDate);
      let params = {
        id: paramsUrl.id,
        symptom: values.symptom,
        symptomStartDate: formatDate(formatStartDate),
        symptomEndDate: values.symptomEndDate ? formatDate(new Date(values.symptomEndDate)) : null,
        active: values.active,
        howStartExperience: values.howStartExperience,
      };

      await putSymptomMutation.mutateAsync(params);

      reset();
      navigate("/user/quick-actions/overall-symptoms");
    } catch (error) {
      console.log("Error : ", error);
    }
  };

  if (isLoading || isLoadingSymptomsCatalog) return "Loading...";

  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <FlexFormContainer headerTitle={"Patient Symptoms"}>
          <Label htmlFor="symptom">Symptom</Label>
          <Controller
            name="symptom"
            control={control}
            render={({ field }) => (
              <>
                <AutoComplete
                  name="symptom"
                  data={symptomsOptions.map((s) => s?.name)}
                  placeholder="Search or enter symptom"
                  size="lg"
                  style={{ minWidth: "300px" }}
                  onChange={(value) => field.onChange(value)}
                  value={field.value}
                />
                {!!errors.symptom && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.symptom?.message}
                  </Text>
                )}
              </>
            )}
          />

          <Label htmlFor="symptomStartDate">Symptom Start Date</Label>
          <Controller
            name="symptomStartDate"
            control={control}
            render={({ field }) => (
              <>
                <DatePicker
                  id={field.name}
                  onChange={(value) => field.onChange(value)}
                  value={field.value != null ? new Date(field.value) : null}
                  format="yyyy-MM-dd"
                  oneTap
                />
                {!!errors.symptomStartDate && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.symptomStartDate.message}
                  </Text>
                )}
              </>
            )}
          />
          <Label htmlFor="symptomEndDate">Symptom End Date</Label>
          <Controller
            name="symptomEndDate"
            control={control}
            render={({ field }) => (
              <>
                <DatePicker
                  id={field.name}
                  onChange={(value) => field.onChange(value)}
                  value={field.value != null ? new Date(field.value) : null}
                  format="yyyy-MM-dd"
                  oneTap
                />
                {!!errors?.symptomEndDate && (
                  <Text
                    variation="error"
                    as="p"
                    lineHeight="0.5em"
                    fontWeight={400}
                    fontSize="0.8em"
                    fontStyle="normal"
                    textDecoration="none"
                    width="30vw"
                    style={{ marginLeft: "10px" }}
                  >
                    {errors.symptomEndDate.message}
                  </Text>
                )}
              </>
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <SwitchField
                label="Is Active?"
                labelPosition="end"
                {...field}
                isChecked={field.value}
                onChange={(event) =>
                  field.onChange(event.target.checked ? 1 : 0)
                }
              />
            )}
          />
          <Controller
            name="howStartExperience"
            control={control}
            render={({ field }) => (
              <TextAreaField
                minWidth="300px"
                label="What do you experience?"
                {...field}
                value={field.value}
                errorMessage={errors.howStartExperience?.message}
                hasError={!!errors.howStartExperience}
              />
            )}
          />

          <Flex gap="12" direction="column">
            <Button variation="primary" type="submit">
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
